import type {
  OperationsGroupDetail,
  OperationsGroupPage,
  ServiceOperationsOverview,
} from "@reminder-tracker/backend-contracts";
import { useMutation, useQuery } from "convex/react";
import { useState } from "react";

import {
  getServiceOperationsOverview,
  getServiceOperationsGroupDetail,
  listServiceOperationsGroups,
  openServiceOperationsInvestigation,
} from "../../backend/functions";
import {
  AdministrationAuditDetails,
  AdministrationImpactPanel,
  AdministrationPageHeader,
  AdministrationState,
  AdministrationStatusSummary,
  AdministrationWorkSurface,
} from "../../ui/administration-surface";
import {
  exactUtcTimestamp,
  formatAdministrationTime,
  formatAdministrationWindow,
} from "../../ui/administration-time";

function reference(prefix: "corr"): string {
  if (typeof globalThis.crypto.randomUUID !== "function")
    throw new Error("A secure reference generator is unavailable.");
  return `${prefix}_${globalThis.crypto.randomUUID().replaceAll("-", "")}`;
}

function timestamp(value: number): string {
  return formatAdministrationTime(value);
}

function displayedCount(value: number | null): string {
  return value === null ? "Unavailable" : String(value);
}

export function ServiceOperationsView({
  overview,
  page,
  onOpenInvestigation,
  opening = false,
  detail,
  onSelectGroup,
  onBackFromDetail,
  onContinue,
}: Readonly<{
  overview: ServiceOperationsOverview;
  page: OperationsGroupPage | null;
  onOpenInvestigation: () => void;
  opening?: boolean;
  detail?: OperationsGroupDetail | null;
  onSelectGroup?: (reference: string) => void;
  onBackFromDetail?: () => void;
  onContinue?: () => void;
}>): React.JSX.Element {
  const selectedDetail = detail ?? null;
  const measures = [
    ["Active groups", overview.groups],
    ["Configured reminders", overview.configuredReminders],
    ["Due occurrences", overview.dueOccurrences],
    ["Delivery intents", overview.deliveryIntents],
    ["Provider attempts", overview.attempts],
  ] as const;
  const actionSignalCount = [
    overview.failures,
    overview.supportHealth.missedReviewAim,
    overview.supportHealth.noticeFailures,
  ].filter((item) => item.value !== null && item.value > 0).length;
  const incompleteEvidence = [
    overview.groups,
    overview.configuredReminders,
    overview.dueOccurrences,
    overview.deliveryIntents,
    overview.attempts,
    overview.failures,
    overview.unknown,
  ].some((item) => item.completeness !== "complete");
  const monitorSignalCount =
    [
      overview.unknown,
      overview.supportHealth.targetRisk,
      overview.supportHealth.newOrUnassigned,
    ].filter((item) => item.value !== null && item.value > 0).length +
    (incompleteEvidence || overview.evidence.freshness !== "current" ? 1 : 0);
  const healthySignalCount =
    actionSignalCount === 0 && monitorSignalCount === 0 && overview.evidence.freshness === "current"
      ? 1
      : 0;
  return (
    <section aria-label="Service health investigation" className="service-operations">
      <AdministrationStatusSummary
        items={[
          {
            label: "Needs action",
            tone: "action",
            value: actionSignalCount,
            title: "Action signal types",
            description: "Failures, missed support aims or failed support notices.",
          },
          {
            label: "Monitor",
            tone: "monitor",
            value: monitorSignalCount,
            title: "Monitor signal types",
            description: "Unknown, approaching, unassigned or incomplete evidence.",
          },
          {
            label: "Healthy",
            tone: "healthy",
            value: healthySignalCount,
            title: "Complete current evidence",
            description: "Shown only when no action or monitoring signal is present.",
          },
        ]}
      />
      <AdministrationImpactPanel
        label="Service impact"
        title={
          actionSignalCount > 0
            ? "Service evidence indicates operator attention is needed"
            : monitorSignalCount > 0
              ? "Service evidence is incomplete or needs monitoring"
              : "No known service issue is indicated in this complete window"
        }
        description="Open a bounded investigation before taking any containment or recovery action. The summary never exposes household names, reminder wording or destinations."
        action={
          <button type="button" disabled={opening} onClick={onOpenInvestigation}>
            {opening ? "Opening investigation…" : "Open bounded investigation"}
          </button>
        }
        evidence={
          <p>
            Values count signal types, not affected customers. Missing or incomplete source evidence
            is treated as monitoring, never as zero.
          </p>
        }
        tone={actionSignalCount > 0 ? "action" : monitorSignalCount > 0 ? "monitor" : "healthy"}
      />
      <details className="inline-details service-health-evidence">
        <summary>Current service activity and support context</summary>
        <div className="section-head">
          <div>
            <h2 id="service-operations-title">Current service activity</h2>
            <p>
              Operational counts only. Household names, reminder wording and destinations are never
              available here.
            </p>
          </div>
          <span>{overview.window === "24-hours" ? "Last 24 hours" : "Last 7 days"}</span>
        </div>
        <p className={`evidence-state ${overview.evidence.freshness}`} role="status">
          <strong>
            {overview.evidence.freshness === "current"
              ? "Current evidence"
              : overview.evidence.freshness === "delayed"
                ? "Evidence delayed"
                : "Evidence unavailable"}
          </strong>
          <span>
            {overview.evidence.completeness === "complete"
              ? "All admitted source measures are complete."
              : "Some source measures are incomplete or unavailable; missing evidence is not shown as zero."}
          </span>
          <time dateTime={new Date(overview.evidence.endAtEpochMilliseconds).toISOString()}>
            Window ended {timestamp(overview.evidence.endAtEpochMilliseconds)}
          </time>
        </p>
        <dl className="operations-measures">
          {measures.map(([label, count]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{displayedCount(count.value)}</dd>
              <span>{count.completeness}</span>
            </div>
          ))}
        </dl>
        <AdministrationAuditDetails summary="Exact evidence window, source checkpoint and correlation">
          <dl className="audit-register">
            <div>
              <dt>Evidence window</dt>
              <dd>
                {formatAdministrationWindow(
                  overview.evidence.startAtEpochMilliseconds,
                  overview.evidence.endAtEpochMilliseconds,
                )}
              </dd>
            </div>
            <div>
              <dt>Window start (UTC)</dt>
              <dd>
                <time dateTime={exactUtcTimestamp(overview.evidence.startAtEpochMilliseconds)}>
                  {exactUtcTimestamp(overview.evidence.startAtEpochMilliseconds)}
                </time>
              </dd>
            </div>
            <div>
              <dt>Window end (UTC, excluded)</dt>
              <dd>
                <time dateTime={exactUtcTimestamp(overview.evidence.endAtEpochMilliseconds)}>
                  {exactUtcTimestamp(overview.evidence.endAtEpochMilliseconds)}
                </time>
              </dd>
            </div>
            <div>
              <dt>Source checkpoint</dt>
              <dd>{overview.evidence.sourceCheckpoint ?? "Not recorded"}</dd>
            </div>
            <div>
              <dt>Correlation reference</dt>
              <dd className="masked">{overview.correlationId}</dd>
            </div>
          </dl>
        </AdministrationAuditDetails>
        <section className="scheduler-assurance" aria-labelledby="support-health-title">
          <h3 id="support-health-title">Support health</h3>
          <p>
            Aggregate operational signals only. This overview cannot open a support case or reveal a
            customer conversation.
          </p>
          <dl className="operations-measures">
            {(
              [
                ["New or unassigned", overview.supportHealth.newOrUnassigned],
                ["Review aim approaching", overview.supportHealth.targetRisk],
                ["Review aim missed", overview.supportHealth.missedReviewAim],
                ["Notice failures or unknown", overview.supportHealth.noticeFailures],
              ] as const
            ).map(([label, item]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{displayedCount(item.value)}</dd>
                <span>{item.completeness}</span>
              </div>
            ))}
          </dl>
        </section>
      </details>
      <AdministrationWorkSurface
        id="masked-group-register"
        title="Investigate masked groups"
        purpose="Open a purpose-bound 15-minute investigation, then inspect one group using safe aggregate evidence."
        state={{
          label:
            page === null
              ? "Investigation not open"
              : `${page.rows.length} masked ${page.rows.length === 1 ? "group" : "groups"} available`,
          tone: page === null ? "monitor" : "neutral",
        }}
        consequence="Opening an investigation records the operational purpose and permits only bounded, masked evidence. It does not reveal household names, reminder wording or destinations."
        action={
          page === null ? (
            <button type="button" disabled={opening} onClick={onOpenInvestigation}>
              {opening ? "Opening investigation…" : "Open service-health investigation"}
            </button>
          ) : undefined
        }
      >
        {selectedDetail === null ? null : (
          <div className="scheduler-assurance" aria-labelledby="masked-group-detail-title">
            <button type="button" onClick={() => onBackFromDetail?.()}>
              Back to masked register
            </button>
            <h3 id="masked-group-detail-title">{selectedDetail.snapshot.reference}</h3>
            <p>
              Snapshot access: <strong>{selectedDetail.snapshot.containment}</strong> · Current
              access: <strong>{selectedDetail.currentContainment}</strong>
            </p>
            <p>
              {displayedCount(selectedDetail.snapshot.configuredReminderCount)} configured reminders
              · {displayedCount(selectedDetail.snapshot.dueOccurrenceCount)} due occurrences ·
              evidence {selectedDetail.snapshot.completeness}
            </p>
            <h4>Masked people</h4>
            <ul>
              {selectedDetail.people.map((person) => (
                <li key={person.reference}>{person.reference}</li>
              ))}
            </ul>
            <p>
              {selectedDetail.peopleCompleteness === "complete"
                ? "Complete bounded member list."
                : "Member list is incomplete at the certified bound."}
            </p>
          </div>
        )}
        {page === null || selectedDetail !== null ? null : (
          <>
            <table className="timeline operations-register">
              <thead>
                <tr>
                  <th>Group reference</th>
                  <th>Access state</th>
                  <th>Members</th>
                  <th>Reminders</th>
                  <th>Evidence</th>
                </tr>
              </thead>
              <tbody>
                {page.rows.map((row) => (
                  <tr key={row.reference}>
                    <td className="masked" data-label="Group reference">
                      <button
                        id={`operations-${row.reference}`}
                        type="button"
                        onClick={() => onSelectGroup?.(row.reference)}
                      >
                        {row.reference}
                      </button>
                    </td>
                    <td data-label="Access state">{row.containment}</td>
                    <td data-label="Members">{row.memberCountBand}</td>
                    <td data-label="Reminders">{displayedCount(row.configuredReminderCount)}</td>
                    <td data-label="Evidence">{row.completeness}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!page.isDone ? (
              <button type="button" onClick={() => onContinue?.()}>
                Continue searching
              </button>
            ) : null}
          </>
        )}
      </AdministrationWorkSurface>
    </section>
  );
}

export function ProtectedServiceOperations(): React.JSX.Element {
  const [window, setWindow] = useState<"24-hours" | "7-days">("24-hours");
  const [correlationId] = useState(() => reference("corr"));
  const [page, setPage] = useState<OperationsGroupPage | null>(null);
  const [investigationId, setInvestigationId] = useState<string | null>(null);
  const [detail, setDetail] = useState<OperationsGroupDetail | null>(null);
  const [selectedReference, setSelectedReference] = useState<string | null>(null);
  const [referencePrefix, setReferencePrefix] = useState("");
  const [lifecycle, setLifecycle] = useState<"active" | "closed" | null>(null);
  const [health, setHealth] = useState<"attention" | "no-known-issue" | "unknown" | null>(null);
  const [sort, setSort] = useState<"attention" | "reference" | "failures" | "last-activity">(
    "attention",
  );
  const [direction, setDirection] = useState<"ascending" | "descending">("ascending");
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const overview = useQuery(getServiceOperationsOverview, { correlationId, window });
  const open = useMutation(openServiceOperationsInvestigation);
  const list = useMutation(listServiceOperationsGroups);
  const getDetail = useMutation(getServiceOperationsGroupDetail);
  const load = async (id: string, cursor: string | null) =>
    await list({
      correlationId: reference("corr"),
      investigationId: id,
      referencePrefix: referencePrefix.trim() === "" ? null : referencePrefix.trim().toUpperCase(),
      lifecycle,
      health,
      sort,
      direction,
      cursor,
      pageSize: 25,
    });
  if (overview === undefined)
    return (
      <AdministrationState
        state="loading"
        title="Preparing service health"
        message="Checking bounded aggregate evidence for the selected window."
      />
    );
  return (
    <>
      <AdministrationPageHeader
        title="Service health"
        purpose="See where the service may need attention, then open a bounded investigation when necessary."
        status={{
          label:
            overview.evidence.freshness === "current"
              ? "Healthy"
              : overview.evidence.freshness === "delayed"
                ? "Monitor"
                : "Needs attention",
          tone:
            overview.evidence.freshness === "current"
              ? "healthy"
              : overview.evidence.freshness === "delayed"
                ? "monitor"
                : "action",
        }}
      />
      <div className="operations-window" aria-label="Evidence window">
        <button
          type="button"
          aria-pressed={window === "24-hours"}
          onClick={() => setWindow("24-hours")}
        >
          24 hours
        </button>
        <button
          type="button"
          aria-pressed={window === "7-days"}
          onClick={() => setWindow("7-days")}
        >
          7 days
        </button>
      </div>
      <ServiceOperationsView
        overview={overview}
        page={page}
        opening={opening}
        detail={detail}
        onBackFromDetail={() => {
          setDetail(null);
          if (selectedReference !== null)
            globalThis.setTimeout(
              () => globalThis.document.getElementById(`operations-${selectedReference}`)?.focus(),
              0,
            );
        }}
        onSelectGroup={(groupReference) => {
          if (investigationId === null) return;
          setSelectedReference(groupReference);
          void getDetail({
            correlationId: reference("corr"),
            investigationId,
            reference: groupReference,
          })
            .then(setDetail)
            .catch(() =>
              setError(
                "This masked group detail is unavailable. Refresh the investigation and try again.",
              ),
            );
        }}
        onContinue={() => {
          if (investigationId === null || page === null || page.continueCursor === null) return;
          setError(null);
          void load(investigationId, page.continueCursor)
            .then(setPage)
            .catch(() =>
              setError(
                "The next bounded page is unavailable. Refresh the investigation before continuing.",
              ),
            );
        }}
        onOpenInvestigation={() => {
          setOpening(true);
          setError(null);
          const openCorrelationId = reference("corr");
          void open({ correlationId: openCorrelationId, purpose: "service-health", window })
            .then(async (opened) => {
              setInvestigationId(opened.investigationId);
              const result = await load(opened.investigationId, null);
              setPage(result);
            })
            .catch(() =>
              setError(
                "The bounded evidence view is still being prepared. Try opening it again shortly.",
              ),
            )
            .finally(() => setOpening(false));
        }}
      />
      {error === null ? null : <p role="alert">{error}</p>}
      {investigationId === null ? null : (
        <div className="operations-window" aria-label="Masked register controls">
          <label>
            Masked reference prefix
            <input
              value={referencePrefix}
              onChange={(event) => setReferencePrefix(event.target.value)}
            />
          </label>
          <label>
            Lifecycle
            <select
              value={lifecycle ?? "all"}
              onChange={(event) =>
                setLifecycle(
                  event.target.value === "all" ? null : (event.target.value as "active" | "closed"),
                )
              }
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="closed">Closed</option>
            </select>
          </label>
          <label>
            Health evidence
            <select
              value={health ?? "all"}
              onChange={(event) =>
                setHealth(
                  event.target.value === "all"
                    ? null
                    : (event.target.value as "attention" | "no-known-issue" | "unknown"),
                )
              }
            >
              <option value="all">All</option>
              <option value="attention">Needs attention</option>
              <option value="no-known-issue">No known issue</option>
              <option value="unknown">Unknown</option>
            </select>
          </label>
          <label>
            Sort
            <select value={sort} onChange={(event) => setSort(event.target.value as typeof sort)}>
              <option value="attention">Attention</option>
              <option value="reference">Reference</option>
              <option value="failures">Failures</option>
              <option value="last-activity">Last activity</option>
            </select>
          </label>
          <button
            type="button"
            onClick={() => {
              setError(null);
              void load(investigationId, null)
                .then(setPage)
                .catch(() =>
                  setError(
                    "The filter could not be applied. Start a fresh investigation if this view expired.",
                  ),
                );
            }}
          >
            Apply filters
          </button>
          <button
            type="button"
            onClick={() =>
              setDirection((value) => (value === "ascending" ? "descending" : "ascending"))
            }
          >
            {direction === "ascending" ? "Ascending" : "Descending"}
          </button>
        </div>
      )}
    </>
  );
}
